import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  FormControl,
  FormLabel,
  Input,
  Select,
  Text,
  VStack,
  IconButton,
  CloseButton,
  Image,
  Grid,
  GridItem,
  useToast,
} from "@chakra-ui/react";
import axios from "axios";
import { Field, Form, Formik } from "formik";
import React, { useState } from "react";
import { FiUpload } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import * as Yup from "yup";

interface AddNotificationDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  getData: () => void;
}

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB
const SUPPORTED_FILE_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];

const validationSchema = Yup.object().shape({
  template_name: Yup.string().required("Template name is required"),
  title: Yup.string().required("Title is required"),
  message: Yup.string().required("Message is required"),
  notificationType: Yup.string().required("Notification type is required"),
  // image: Yup.mixed()
  //   .test("fileSize", "File too large", (value) => {
  //     if (!value) return true;
  //     return value.size <= MAX_FILE_SIZE;
  //   })
  //   .test("fileType", "Unsupported file type", (value) => {
  //     if (!value) return true;
  //     return SUPPORTED_FILE_TYPES.includes(value.type);
  //   }),
});

const AddNotificationsDrawer: React.FC<any> = ({
  isOpen,
  onClose,
  getData,
  notification,
}) => {
  const [selectedImage, setSelectedImage] = useState<any>(() => {
    if (notification?.image) {
      return {
        file: null,
        preview: `${process.env.REACT_APP_IMAGE_BASE_URL}/${notification.image.url}`,
      };
    }
    return {
      file: null,
      preview: "",
    };
  });

  const toast = useToast();

  const initialValues = {
    template_name: notification?.template_name || "",
    title: notification?.title || "",
    message: notification?.message || "",
    notificationType: notification?.notificationType || "",
    image: notification?.image?._id || "",
  };

  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const file = event.target.files?.[0] || null;
    if (file) {
      setSelectedImage({
        file,
        preview: URL.createObjectURL(file),
      });

      // setFieldValue("bannerImage", file);

      try {
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/upload/media`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        setFieldValue("image", response.data.data._id || file.name);
      } catch (error) {
        console.error("Image upload failed:", error);
      }
    }
  };

  const removeImage = (setFieldValue: (field: string, value: any) => void) => {
    setSelectedImage({
      file: null,
      preview: "",
    });
    setFieldValue("image", null);
    // setFieldValue("imagename", "");
  };

  const handleSubmit = (values: any, { setSubmitting }: any) => {
    // console.log("Form values:", values);

    let payload = {
      template_name: values.template_name,
      title: values.title,
      message: values.message,
      notificationType: values.notificationType,
      image: values.image,
    };
    // console.log("Payload:", payload);

    if (notification?._id) {
      axios({
        method: "patch",
        url: `${process.env.REACT_APP_BASE_URL}/admin/notification/${notification._id}`,
        data: payload,
      })
        .then((response) => {
          setSubmitting(false);
          onClose(); // Optionally close the drawer on submit
          getData(); // Optionally refresh the data after submit
          toast({
            title: "Notification successfully edit.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to edit",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setSubmitting(false);
          // onClose();
          console.error("API Error:", error);
        });
    } else {
      axios({
        method: "post",
        url: `${process.env.REACT_APP_BASE_URL}/admin/notification`,
        data: payload,
      })
        .then((response) => {
          setSubmitting(false);
          onClose(); // Optionally close the drawer on submit
          getData(); // Optionally refresh the data after submit
          toast({
            title: "Notification successfully add.",
            status: "success",
            duration: 3000,
            isClosable: true,
          });
        })
        .catch((error) => {
          toast({
            title: "Failed to add",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          setSubmitting(false);
          // onClose();
          console.error("API Error:", error);
        });
    }
  };

  return (
    <>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, setFieldValue, submitForm }) => (
          <Form>
            <Drawer
              isOpen={isOpen}
              placement="right"
              size={"md"}
              onClose={() => {
                onClose();
              }}
              // closeOnOverlayClick={busy}
            >
              <DrawerOverlay />
              <DrawerContent>
                <DrawerHeader
                  borderBottomWidth="1px"
                  bg="white"
                  position="relative"
                  px={6}
                  py={4}
                >
                  {notification?._id ? "Edit Notification" : "Add Notification"}
                  <CloseButton
                    position="absolute"
                    right={4}
                    top={4}
                    onClick={onClose}
                  />
                </DrawerHeader>
                <DrawerBody>
                  <VStack spacing={6} align="stretch">
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        Add template image
                      </Text>
                      <Button
                        leftIcon={<FiUpload />}
                        variant="outline"
                        color="red.500"
                        borderStyle="dashed"
                        borderColor="red.500"
                        onClick={() =>
                          document.getElementById("image-upload2")?.click()
                        }
                        w="fit-content"
                      >
                        Upload Image
                      </Button>
                      <Input
                        id="image-upload2"
                        type="file"
                        accept="image/*"
                        display="none"
                        onChange={(e) => handleImageUpload(e, setFieldValue)}
                      />
                      <Text fontSize="sm" color="gray.500" mt={1}>
                        You can upload 1 image only. Supported file types are
                        .png, .jpg, .jpeg
                      </Text>
                      {selectedImage.preview && (
                        <Grid templateColumns="repeat(1, 1fr)" gap={2} mt={2}>
                          <GridItem position="relative">
                            <Image
                              src={selectedImage.preview}
                              alt="Banner Preview"
                              boxSize="100px"
                              objectFit="cover"
                              borderRadius="6px"
                            />
                            <IconButton
                              aria-label="Remove image"
                              color="#E24441"
                              icon={<RiDeleteBin6Line />}
                              size="sm"
                              position="absolute"
                              top={2}
                              right={2}
                              onClick={() => removeImage(setFieldValue)}
                            />
                          </GridItem>
                        </Grid>
                      )}
                    </Box>

                    <FormControl>
                      <FormLabel>Template name</FormLabel>
                      <Field
                        as={Input}
                        name="template_name"
                        placeholder="Enter Template name"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Title</FormLabel>
                      <Field
                        as={Input}
                        name="title"
                        placeholder="Enter title"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Message</FormLabel>
                      <Field
                        as={Input}
                        name="message"
                        placeholder="Enter message"
                      />
                    </FormControl>

                    <FormControl>
                      <FormLabel>Notification type</FormLabel>
                      <Field
                        as={Select}
                        name="notificationType"
                        placeholder="Select notification type"
                      >
                        <option value="PROMOTIONAL">Promotional</option>
                        {/* <option value="BIRTHDAY">Birthday</option>
                        <option value="ANNIVERSARY">Anniversary</option> */}
                        <option value="OFFER">Offer</option>
                        <option value="FLASH_SALE">Flash Sale</option>
                        <option value="GENERAL">General</option>
                      </Field>
                    </FormControl>
                  </VStack>
                </DrawerBody>

                <DrawerFooter borderTopWidth="1px" px={6} py={4}>
                  <Button variant="outline" mr={3} onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    colorScheme="red"
                    type="submit"
                    isLoading={isSubmitting}
                    onClick={submitForm}
                  >
                    {notification?._id
                      ? "Edit Notification"
                      : "Add Notification"}
                  </Button>
                </DrawerFooter>
              </DrawerContent>
            </Drawer>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default AddNotificationsDrawer;
